import { Add } from '@mui/icons-material';
import { Dialog, DialogContent, DialogTitle, IconButton, TextField } from '@mui/material';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { userQuery } from '../../../core/authentication/queries';
import { CreateProjectCommand } from '../../../core/project/commands/CreateProjectCommand';
import { InitProjectCommand } from '../../../core/project/commands/InitProjectCommand';
import { LoadProjectsCommand } from '../../../core/project/commands/LoadProjectsCommand';
import { currentProjectIdQuery } from '../../../core/project/queries';
import { setCurrentProjectId } from '../../../core/project/state';
import { useAppDispatch, useAppSelector } from '../../../types';
import BaseButton from '../../design_system/inputs/buttons/BaseButton';
import { SearchableSelect } from '../../design_system/inputs/select/SearchableSelect';
import { theme } from '../../legacy/style/theme';

export interface Project {
    id: string;
    name: string;
    organizationId: string;
    createdAt: string;
    updatedAt: string;
}

export const ProjectSelector: FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const user = useAppSelector(userQuery);
    const currentProjectId = useAppSelector(currentProjectIdQuery);
    const [projects, setProjects] = useState<Project[]>([]);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');

    const loadProjects = useCallback(async () => {
        if (!user) {
            return;
        }

        try {
            const loadedProjects = await dispatch(LoadProjectsCommand()).unwrap();
            setProjects(loadedProjects);
        } catch (err) {
            console.error('Error loading projects:', err);
        }
    }, [dispatch, user]);

    useEffect(() => {
        loadProjects();
    }, [loadProjects]);

    // Auto-select default project if it's the only one
    useEffect(() => {
        if (projects.length === 1 && projects[0]?.name === 'Projet initial' && currentProjectId !== projects[0].id) {
            dispatch(setCurrentProjectId(projects[0].id));
            dispatch(InitProjectCommand());
        }
    }, [projects, currentProjectId, dispatch]);

    const handleProjectChange = useCallback(
        (projectId: string) => {
            if (projectId === '__search__') {
                return;
            }
            dispatch(setCurrentProjectId(projectId === '' ? null : projectId));
            dispatch(InitProjectCommand());
        },
        [dispatch],
    );

    const handleCreateProject = useCallback(async () => {
        if (!newProjectName.trim()) {
            return;
        }

        try {
            const newProject = await dispatch(CreateProjectCommand({ name: newProjectName.trim() })).unwrap();

            setProjects((prev) => {
                if (newProject) {
                    return [newProject, ...prev];
                }

                return prev;
            });
            setCreateDialogOpen(false);
            setNewProjectName('');
            await dispatch(InitProjectCommand());
        } catch (err) {
            console.error('Error creating project:', err);
        }
    }, [dispatch, newProjectName]);

    if (!user) {
        return null;
    }

    return (
        <>
            <SearchableSelect
                value={currentProjectId}
                onChange={(projectId) => handleProjectChange(projectId ?? '')}
                options={projects.map((project) => ({
                    value: project.id,
                    label: project.name,
                }))}
                placeholder={t('projectList.selectProject')}
                searchPlaceholder={t('projectList.searchProject')}
                sx={{ minWidth: 250, backgroundColor: theme.color.white.main }}
            />
            <IconButton
                onClick={() => setCreateDialogOpen(true)}
                size="small"
                sx={{ ml: 1, backgroundColor: theme.color.white.main, width: '36px', height: '36px' }}
                title={t('projectList.createProject')}
            >
                <Add />
            </IconButton>

            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('projectList.createProject')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('projectList.projectName')}
                        fullWidth
                        variant="standard"
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleCreateProject();
                            }
                        }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
                        <BaseButton onClick={() => setCreateDialogOpen(false)} variant="secondary">
                            {t('forms.cancel')}
                        </BaseButton>
                        <BaseButton onClick={handleCreateProject} disabled={!newProjectName.trim()} variant="primary">
                            {t('forms.save')}
                        </BaseButton>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
